import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ServiceRule } from "@/components/booking/ServiceRule";
import { Users, Sparkles, Clock, Crown, UserPlus, Send, Plus, Check, Trash2, CalendarDays } from "lucide-react";

const SEG = {
  new: { label: "New", icon: UserPlus, color: "text-sky-600 bg-sky-50", hint: "Added this month — resets on the 1st" },
  active: { label: "Active", icon: Users, color: "text-emerald-600 bg-emerald-50", hint: "Visited in the last 3 months or booked in" },
  lapsed: { label: "Lapsed", icon: Clock, color: "text-amber-600 bg-amber-50", hint: "No visit in 3 months and nothing booked" },
  vip: { label: "VIP", icon: Crown, color: "text-violet-600 bg-violet-50", hint: "Active with $1,000+ spent or 8+ visits" },
};

export default function CRM({ embedded = false }) {
  const [data, setData] = useState(null);
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [seg, setSeg] = useState(sp.get("seg") || "lapsed");
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [staff, setStaff] = useState([]);
  const [filterAssignee, setFilterAssignee] = useState("");
  const [wb, setWb] = useState({ winback_auto: false, winback_message: "", winback_weeks: 6, winback_send_hour: 10, winback_services: [], winback_exclude_services: [] });
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/crm/overview").then(({ data }) => setData(data)).catch(() => {});
    api.get("/crm/tasks").then(({ data }) => setTasks(data)).catch(() => {});
    api.get("/staff").then(({ data }) => setStaff(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
    api.get("/crm/settings").then(({ data }) => setWb(data)).catch(() => {});
  };
  useEffect(load, []);

  const addTask = async () => {
    if (!newTask.trim()) return;
    try {
      await api.post("/crm/tasks", { title: newTask.trim(), assigned_to: assignTo || null, due_date: dueDate || null });
      setNewTask(""); setAssignTo(""); setDueDate(""); load(); toast.success("Task added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const saveWinback = async (patch) => {
    const next = { ...wb, ...patch };
    setWb(next);
    try { await api.put("/crm/settings", next); toast.success("Win-back settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const toggleTask = async (t) => {
    try { await api.patch(`/crm/tasks/${t.id}`, { status: t.status === "open" ? "done" : "open" }); load(); }
    catch { toast.error("Failed"); }
  };
  const delTask = async (t) => { try { await api.delete(`/crm/tasks/${t.id}`); load(); } catch { toast.error("Failed"); } };

  const runCampaign = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/crm/rebooking", { segment: seg });
      toast.success(`${data.sent} sent · ${data.skipped} skipped${data.sms_enabled ? "" : " (SMS not enabled — simulated)"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSending(false); }
  };

  const counts = data?.counts || {};
  const list = data?.segments?.[seg] || [];

  return (
    <div className={embedded ? "space-y-6" : "space-y-8 p-6 lg:p-8"} data-testid="crm-page">
      {!embedded && <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><Sparkles className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-heading font-bold text-3xl">CRM</h1>
          <p className="text-muted-foreground text-sm">Segment clients, track follow-ups, and win back lapsed clients.</p>
        </div>
      </div>}

      <Tabs defaultValue="segments" className="space-y-6">
        <TabsList data-testid="crm-tabs">
          <TabsTrigger value="segments" data-testid="tab-segments"><Users className="h-4 w-4 mr-1.5" />Segments</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><Check className="h-4 w-4 mr-1.5" />Tasks</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns"><Send className="h-4 w-4 mr-1.5" />Re-booking</TabsTrigger>
        </TabsList>

        <TabsContent value="segments" className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(SEG).map(([k, s]) => (
              <button key={k} onClick={() => (embedded ? navigate(`/crm?seg=${k}`) : setSeg(k))} data-testid={`seg-${k}`}
                className={`p-5 rounded-2xl border text-left transition-colors ${!embedded && seg === k ? "border-primary ring-1 ring-primary" : "hover:border-primary/40"}`}>
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-2 ${s.color}`}><s.icon className="h-5 w-5" /></div>
                <div className="text-2xl font-heading font-bold">{counts[k] ?? "—"}</div>
                <div className="text-xs text-muted-foreground">{s.label} clients</div>
                <div className="text-[10px] text-muted-foreground/80 mt-1 leading-snug" data-testid={`seg-hint-${k}`}>{s.hint}</div>
              </button>
            ))}
          </div>
          {embedded && <p className="text-xs text-muted-foreground -mt-2" data-testid="seg-embedded-hint">Tap a segment to see the client list.</p>}
          {!embedded && <Card className="p-6 rounded-2xl" data-testid="seg-list">
            <h2 className="font-heading font-bold text-lg mb-4">{SEG[seg].label} clients ({list.length})</h2>
            {!list.length ? <p className="text-sm text-muted-foreground">No clients in this segment.</p> : (
              <div className="divide-y">
                {list.slice(0, 100).map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`seg-client-${c.id}`}>
                    <div><span className="font-medium">{c.name}</span> <span className="text-muted-foreground">· {c.phone || c.email || "no contact"}</span></div>
                    <div className="text-muted-foreground text-xs">{c.visits} visits · ${c.total_spent} · last {c.last_visit || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>}
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card className="p-6 rounded-2xl space-y-4" data-testid="crm-tasks">
            <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2">
              <Input value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} placeholder="Add a follow-up task…" className="h-11 rounded-lg" data-testid="task-input" />
              <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="h-11 rounded-lg border px-3 text-sm bg-background" data-testid="task-assignee">
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-11 rounded-lg" data-testid="task-due" />
              <Button onClick={addTask} className="h-11 rounded-lg shrink-0" data-testid="task-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Show:</span>
              <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} className="h-9 rounded-lg border px-2 text-sm bg-background" data-testid="task-filter">
                <option value="">Everyone</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {(() => { const shown = filterAssignee ? tasks.filter((t) => t.assigned_to === filterAssignee) : tasks; return !shown.length ? <p className="text-sm text-muted-foreground">No tasks.</p> : (
              <div className="divide-y">
                {shown.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`task-${t.id}`}>
                    <button onClick={() => toggleTask(t)} className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${t.status === "done" ? "bg-primary border-primary text-white" : "border-neutral-300"}`} data-testid={`task-toggle-${t.id}`}>
                      {t.status === "done" && <Check className="h-3.5 w-3.5" />}
                    </button>
                    <div className={`flex-1 text-sm ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                      {t.title}{t.client_name ? <span className="text-muted-foreground"> · {t.client_name}</span> : ""}
                      <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                        {t.assigned_name && <span className="flex items-center gap-1"><UserPlus className="h-3 w-3" />{t.assigned_name}</span>}
                        {t.due_date && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{t.due_date}</span>}
                      </div>
                    </div>
                    <button onClick={() => delTask(t)} className="text-muted-foreground hover:text-destructive" data-testid={`task-del-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            ); })()}
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <Card className="p-6 rounded-2xl space-y-4 bg-primary/5 border-primary/20" data-testid="crm-winback">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-lg">Automatic rebook reminders</h2>
                <p className="text-sm text-muted-foreground">Text clients who have <b>no upcoming booking</b> a friendly rebook nudge automatically, a set time after their last visit. Each client is messaged once — and again only if they come back and lapse again.</p>
              </div>
              <Switch checked={!!wb.winback_auto} onCheckedChange={(v) => saveWinback({ winback_auto: v })} data-testid="winback-toggle" />
            </div>
            {wb.winback_auto && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span>Send</span>
                  <Input type="number" min={1} max={52} value={wb.winback_weeks ?? 6}
                    onChange={(e) => setWb((s) => ({ ...s, winback_weeks: parseInt(e.target.value || "0", 10) }))}
                    onBlur={() => saveWinback({})} className="h-9 w-20 rounded-lg" data-testid="winback-weeks" />
                  <span>weeks after their last visit, at</span>
                  <select value={wb.winback_send_hour ?? 10}
                    onChange={(e) => saveWinback({ winback_send_hour: parseInt(e.target.value, 10) })}
                    className="h-9 rounded-lg border border-input bg-background px-2 text-sm" data-testid="winback-hour">
                    {Array.from({ length: 14 }, (_, i) => i + 7).map((h) => (
                      <option key={h} value={h}>{((h % 12) || 12)}:00 {h < 12 ? "am" : "pm"}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Message (leave blank for a friendly default)</Label>
                  <div className="rounded-xl border bg-background p-3 space-y-3" data-testid="winback-rules">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">SMS rules</div>
                    <ServiceRule label="For a last visit containing:" value={wb.winback_services} onChange={(v) => saveWinback({ winback_services: v })} testid="winback-for" />
                    <ServiceRule label="And the client has no future appointment containing:" value={wb.winback_exclude_services} onChange={(v) => saveWinback({ winback_exclude_services: v })} testid="winback-unless" />
                    <p className="text-[11px] text-muted-foreground">Per-service delays (Services → "Rebook reminder after") still override the weeks above.</p>
                  </div>
                  <Textarea value={wb.winback_message || ""} onChange={(e) => setWb((s) => ({ ...s, winback_message: e.target.value }))} onBlur={() => saveWinback({})} rows={2} className="rounded-lg" placeholder="We miss you {name}! Book your next visit: {link}" data-testid="winback-message" />
                  <p className="text-[11px] text-muted-foreground">Use {"{name}"}, {"{clinic}"}, {"{service}"}, {"{link}"}. Only clients with a mobile and no upcoming appointment are texted. Need a different delay per treatment (brows 3 weeks, laser 6)? Set <b>Rebook reminder after</b> on each service in Services — it overrides the number above.</p>
                </div>
              </div>
            )}
          </Card>
          <Card className="p-6 rounded-2xl space-y-4" data-testid="crm-campaign">
            <h2 className="font-heading font-bold text-lg">Automated re-booking</h2>
            <p className="text-sm text-muted-foreground">Send a booking nudge (SMS) to everyone in a segment. Lapsed clients are the usual target.</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(SEG).map(([k, s]) => (
                <Button key={k} variant={seg === k ? "default" : "outline"} className="rounded-lg" onClick={() => setSeg(k)} data-testid={`campaign-seg-${k}`}>{s.label} ({counts[k] ?? 0})</Button>
              ))}
            </div>
            <Button onClick={runCampaign} disabled={sending} className="rounded-lg" data-testid="campaign-send"><Send className="h-4 w-4 mr-1.5" />{sending ? "Sending…" : `Send to ${SEG[seg].label} (${counts[seg] ?? 0})`}</Button>
            <p className="text-xs text-muted-foreground">Only clients with a mobile number receive the text. Needs SMS enabled in Integrations to actually send.</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
